import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import { formatCurrency, formatNumber } from '../closeout/closeoutHelpers';
import {
  globalSupplyChainClause,
  tariffsAndDutiesClause,
  standardTermsClause,
} from './clauseContent';

// Register fonts for better typography
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
});

// Create optimized styles for proper table sizing and page breaks
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: '0.5in', // Reduced padding for more content space
    fontFamily: 'Roboto',
    fontSize: 10, // Reduced base font size
    lineHeight: 1.3,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerLeft: {
    flex: 1,
    paddingRight: 10,
  },
  headerRight: {
    flex: 1,
    textAlign: 'right',
    paddingLeft: 10,
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 16,
  },
  sectionHeader: {
    backgroundColor: '#1976d2',
    color: '#ffffff',
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 4,
  },
  sectionContent: {
    marginBottom: 8,
    padding: 6,
  },
  // Work Description Styles
  workDescription: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 8,
  },
  workDescriptionParagraph: {
    marginBottom: 6,
  },
  workDescriptionList: {
    paddingLeft: 16,
    marginBottom: 6,
  },
  workDescriptionListItem: {
    flexDirection: 'row',
    marginBottom: 3,
    alignItems: 'flex-start',
  },
  bulletPoint: {
    width: 12,
    fontSize: 10,
    paddingTop: 1,
  },
  listItemText: {
    flex: 1,
    fontSize: 10,
    lineHeight: 1.3,
  },
  // Optimized Table Styles
  table: {
    width: '100%',
    marginBottom: 8,
    border: '1px solid #e0e0e0',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
    minHeight: 20,
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: '#e3f2fd',
    fontWeight: 'bold',
    minHeight: 24,
  },
  // Base table cell with proper text wrapping
  tableCell: {
    padding: 3,
    fontSize: 8, // Smaller font for tables
    borderRightWidth: 0.5,
    borderRightColor: '#e0e0e0',
    lineHeight: 1.2,
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  // Text content that can wrap within cells
  tableCellText: {
    fontSize: 8,
    lineHeight: 1.2,
  },
  tableCellTextBold: {
    fontSize: 8,
    lineHeight: 1.2,
    fontWeight: 'bold',
  },
  totalRow: {
    backgroundColor: '#e3f2fd',
    fontWeight: 'bold',
    minHeight: 24,
  },
  // General Text Styles
  text: {
    fontSize: 10,
    lineHeight: 1.3,
  },
  boldText: {
    fontWeight: 'bold',
    fontSize: 10,
    marginBottom: 5,
  },
  smallText: {
    fontSize: 8,
    color: '#666666',
  },
  costSummaryTotal: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1976d2',
  },
  // Section-specific styles
  extensionMaterialsSection: {
    marginTop: 6,
    marginBottom: 12,
  },
  // Keep together section
  keepTogetherSection: {
    minHeight: 50,
    pageBreakInside: 'avoid',
  },
  // Signature and footer
  signatureSection: {
    marginBottom: 20,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    height: 40,
    marginVertical: 10,
  },
  footer: {
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    paddingTop: 10,
    alignItems: 'center',
  },
  clause: {
    marginBottom: 10,
    padding: 5,
  },
});

interface ChangeOrderReportPDFProps {
  // All the same props as ChangeOrderReport
  data: any; // Pre-processed data from the main component
  settings: any; // Report settings
}

// HTML Entity decoder
const decodeHtmlEntities = (text: string): string => {
  const entityMap: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&nbsp;': ' ',
    '&ndash;': '–',
    '&mdash;': '—',
    '&hellip;': '…',
  };

  return text.replace(/&[a-zA-Z0-9#]+;/g, entity => entityMap[entity] || entity);
};

// Universal HTML Element Node interface
interface HtmlNode {
  type: 'element' | 'text';
  tagName?: string;
  attributes?: Record<string, string>;
  children?: HtmlNode[];
  content?: string;
  styles?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
  };
}

// PDF Rendering instruction interface
interface RenderInstruction {
  type: 'text' | 'heading' | 'paragraph' | 'list' | 'listItem' | 'break' | 'section';
  content?: string;
  level?: number;
  styles?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: number;
  };
  children?: RenderInstruction[];
  listType?: 'ul' | 'ol';
}

// Robust HTML Parser - converts any HTML to a tree structure
class HtmlParser {
  private html: string;
  private position: number = 0;

  constructor(html: string) {
    this.html = html.trim();
    this.position = 0;
  }

  parse(): HtmlNode[] {
    const nodes: HtmlNode[] = [];

    while (this.position < this.html.length) {
      const node = this.parseNode();
      if (node) {
        nodes.push(node);
      }
    }

    return nodes;
  }

  private parseNode(): HtmlNode | null {
    this.skipWhitespace();

    if (this.position >= this.html.length) {
      return null;
    }

    if (this.html[this.position] === '<') {
      return this.parseElement();
    } else {
      return this.parseText();
    }
  }

  private parseElement(): HtmlNode | null {
    const tagMatch = this.html.slice(this.position).match(/^<(\/?[a-zA-Z0-9]+)([^>]*)>/);
    if (!tagMatch) {
      // Not a valid tag, treat as text
      return this.parseText();
    }

    const fullTag = tagMatch[0];
    const tagName = tagMatch[1].toLowerCase();
    const attributes = this.parseAttributes(tagMatch[2]);

    this.position += fullTag.length;

    // Handle self-closing tags
    if (fullTag.endsWith('/>') || ['br', 'hr', 'img'].includes(tagName)) {
      return {
        type: 'element',
        tagName,
        attributes,
        children: [],
      };
    }

    // Handle closing tags (skip them, they're handled by findClosingTag)
    if (tagName.startsWith('/')) {
      return null;
    }

    // Find the matching closing tag and parse children
    const closingTagStart = this.findClosingTag(tagName);
    let children: HtmlNode[] = [];

    if (closingTagStart !== -1) {
      const innerHtml = this.html.slice(this.position, closingTagStart);
      if (innerHtml.trim()) {
        const childParser = new HtmlParser(innerHtml);
        children = childParser.parse();
      }

      // Move position past the closing tag
      const closingTag = `</${tagName}>`;
      this.position = closingTagStart + closingTag.length;
    }

    return {
      type: 'element',
      tagName,
      attributes,
      children,
    };
  }

  private parseText(): HtmlNode {
    let text = '';

    while (this.position < this.html.length && this.html[this.position] !== '<') {
      text += this.html[this.position];
      this.position++;
    }

    return {
      type: 'text',
      content: decodeHtmlEntities(text.trim()),
    };
  }

  private parseAttributes(attrString: string): Record<string, string> {
    const attributes: Record<string, string> = {};
    const attrMatch = attrString.match(/(\w+)=["']([^"']*)["']/g);

    if (attrMatch) {
      attrMatch.forEach(attr => {
        const [, key, value] = attr.match(/(\w+)=["']([^"']*)["']/) || [];
        if (key && value !== undefined) {
          attributes[key] = value;
        }
      });
    }

    return attributes;
  }

  private findClosingTag(tagName: string): number {
    let depth = 1;
    let searchPos = this.position;
    const openTag = `<${tagName}`;
    const closeTag = `</${tagName}>`;

    while (depth > 0 && searchPos < this.html.length) {
      const nextOpen = this.html.indexOf(openTag, searchPos);
      const nextClose = this.html.indexOf(closeTag, searchPos);

      if (nextClose === -1) {
        return -1; // No closing tag found
      }

      if (nextOpen !== -1 && nextOpen < nextClose) {
        // Make sure it's a complete opening tag
        const afterTag = this.html.indexOf('>', nextOpen);
        if (afterTag !== -1 && afterTag < nextClose) {
          depth++;
          searchPos = afterTag + 1;
        } else {
          searchPos = nextOpen + openTag.length;
        }
      } else {
        depth--;
        if (depth === 0) {
          return nextClose;
        }
        searchPos = nextClose + closeTag.length;
      }
    }

    return -1;
  }

  private skipWhitespace(): void {
    while (this.position < this.html.length && /\s/.test(this.html[this.position])) {
      this.position++;
    }
  }
}

// Convert HTML tree to PDF rendering instructions
class HtmlToPdfConverter {
  convert(nodes: HtmlNode[]): RenderInstruction[] {
    return nodes.map(node => this.convertNode(node)).filter(Boolean) as RenderInstruction[];
  }

  private convertNode(node: HtmlNode): RenderInstruction | null {
    if (node.type === 'text') {
      if (!node.content || !node.content.trim()) {
        return null;
      }
      return {
        type: 'text',
        content: node.content,
      };
    }

    if (node.type === 'element') {
      return this.convertElement(node);
    }

    return null;
  }

  private convertElement(node: HtmlNode): RenderInstruction | null {
    const tagName = node.tagName!.toLowerCase();
    const children = node.children ? this.convert(node.children) : [];

    switch (tagName) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return {
          type: 'heading',
          level: parseInt(tagName[1]),
          content: this.extractTextContent(node),
          styles: { bold: true, fontSize: 16 - parseInt(tagName[1]) },
        };

      case 'p':
        return {
          type: 'paragraph',
          children:
            children.length > 0
              ? children
              : [{ type: 'text', content: this.extractTextContent(node) }],
        };

      case 'strong':
      case 'b':
        return {
          type: 'text',
          content: this.extractTextContent(node),
          styles: { bold: true },
        };

      case 'em':
      case 'i':
        return {
          type: 'text',
          content: this.extractTextContent(node),
          styles: { italic: true },
        };

      case 'u':
        return {
          type: 'text',
          content: this.extractTextContent(node),
          styles: { underline: true },
        };

      case 'ul':
      case 'ol':
        return {
          type: 'list',
          listType: tagName as 'ul' | 'ol',
          children: children.filter(child => child.type === 'listItem'),
        };

      case 'li':
        return {
          type: 'listItem',
          children:
            children.length > 0
              ? children
              : [{ type: 'text', content: this.extractTextContent(node) }],
        };

      case 'br':
        return {
          type: 'break',
        };

      case 'div':
      case 'span':
        // Pass through container elements
        return children.length === 1
          ? children[0]
          : {
              type: 'paragraph',
              children,
            };

      default:
        // For unknown tags, extract text content
        const textContent = this.extractTextContent(node);
        return textContent
          ? {
              type: 'text',
              content: textContent,
            }
          : null;
    }
  }

  private extractTextContent(node: HtmlNode): string {
    if (node.type === 'text') {
      return node.content || '';
    }

    if (node.type === 'element' && node.children) {
      return node.children
        .map(child => this.extractTextContent(child))
        .join(' ')
        .trim();
    }

    return '';
  }
}

// Main parsing function - now handles any HTML content
const parseHtmlContent = (html: string): RenderInstruction[] => {
  if (!html || html.trim() === '') {
    return [];
  }

  try {
    const parser = new HtmlParser(html);
    const htmlTree = parser.parse();

    const converter = new HtmlToPdfConverter();
    const renderInstructions = converter.convert(htmlTree);

    return renderInstructions;
  } catch (error) {
    console.error('HTML parsing error:', error);
    // Fallback: treat as plain text
    return [
      {
        type: 'text',
        content: decodeHtmlEntities(html.replace(/<[^>]*>/g, '')),
      },
    ];
  }
};

// Universal PDF Renderer Component for RenderInstructions
interface RendererProps {
  instructions: RenderInstruction[];
  baseStyles?: any;
}

const RenderInstructionsToPdf: React.FC<RendererProps> = ({ instructions, baseStyles = {} }) => {
  return (
    <>
      {instructions.map((instruction, index) => {
        switch (instruction.type) {
          case 'text':
            return (
              <Text
                key={index}
                style={[
                  baseStyles.text || styles.text,
                  instruction.styles?.bold && { fontWeight: 'bold' },
                  instruction.styles?.italic && { fontStyle: 'italic' },
                  instruction.styles?.underline && { textDecoration: 'underline' },
                  instruction.styles?.fontSize && { fontSize: instruction.styles.fontSize },
                ]}
              >
                {instruction.content}
              </Text>
            );

          case 'heading':
            return (
              <Text
                key={index}
                style={[
                  baseStyles.heading || styles.text,
                  {
                    fontSize: instruction.styles?.fontSize || 16 - (instruction.level || 1),
                    fontWeight: 'bold',
                    marginTop: 12,
                    marginBottom: 6,
                  },
                ]}
              >
                {instruction.content}
              </Text>
            );

          case 'paragraph':
            return (
              <View key={index} style={[baseStyles.paragraph || styles.workDescriptionParagraph]}>
                {instruction.children && (
                  <RenderInstructionsToPdf
                    instructions={instruction.children}
                    baseStyles={baseStyles}
                  />
                )}
              </View>
            );

          case 'list':
            return (
              <View key={index} style={[baseStyles.list || styles.workDescriptionList]}>
                {instruction.children && (
                  <RenderInstructionsToPdf
                    instructions={instruction.children}
                    baseStyles={baseStyles}
                  />
                )}
              </View>
            );

          case 'listItem':
            return (
              <View key={index} style={[baseStyles.listItem || styles.workDescriptionListItem]}>
                <Text style={[baseStyles.bulletPoint || styles.bulletPoint]}>
                  {instruction.listType === 'ol' ? `${index + 1}.` : '•'}
                </Text>
                <View style={[baseStyles.listItemText || styles.listItemText]}>
                  {instruction.children ? (
                    <RenderInstructionsToPdf
                      instructions={instruction.children}
                      baseStyles={baseStyles}
                    />
                  ) : (
                    <Text style={baseStyles.text || styles.text}>{instruction.content}</Text>
                  )}
                </View>
              </View>
            );

          case 'break':
            return (
              <Text key={index} style={baseStyles.text || styles.text}>
                {'\n'}
              </Text>
            );

          default:
            return null;
        }
      })}
    </>
  );
};

// Smart section wrapper for intelligent page breaks
interface SmartSectionProps {
  children: React.ReactNode;
  sectionType: 'header' | 'content' | 'table' | 'extensionMaterials' | 'signature';
  forceNewPage?: boolean;
}

const SmartSection: React.FC<SmartSectionProps> = ({
  children,
  sectionType,
  forceNewPage = false,
}) => {
  // Use View with conditional page break
  if (forceNewPage) {
    return <View break>{children}</View>;
  }

  // Keep signatures together
  if (sectionType === 'signature') {
    return <View wrap={false}>{children}</View>;
  }

  return <View>{children}</View>;
};

// Optimized table cell component with intelligent text fitting
interface SmartTableCellProps {
  children: React.ReactNode;
  width: string;
  align?: 'left' | 'right' | 'center';
  isHeader?: boolean;
  isLast?: boolean;
  isBold?: boolean;
  paddingLeft?: number;
}

const SmartTableCell: React.FC<SmartTableCellProps> = ({
  children,
  width,
  align = 'left',
  isHeader = false,
  isLast = false,
  isBold = false,
  paddingLeft,
}) => {
  const cellStyle: any = {
    ...styles.tableCell,
    width,
    textAlign: align,
    ...(paddingLeft && { paddingLeft }),
    ...(isLast && styles.tableCellLast),
  };

  const textStyle: any = {
    ...(isHeader ? styles.tableCellTextBold : styles.tableCellText),
    ...(isBold && { fontWeight: 'bold' }),
  };

  return (
    <View style={cellStyle}>
      {React.isValidElement(children) ? (
        children
      ) : (
        <Text style={textStyle}>{String(children || '')}</Text>
      )}
    </View>
  );
};

export const ChangeOrderReportPDF: React.FC<ChangeOrderReportPDFProps> = ({ data, settings }) => {
  const {
    companyName,
    workDescription,
    pcoNumber,
    isEstimateContext,
    userData,
    accountData,
    projectData,
    contractData,
    estimateData,
    changeOrderData,
    materialBreakdown,
    quoteBreakdown,
    laborBreakdown,
    equipmentBreakdown,
    subcontractorBreakdown,
    generalExpenseBreakdown,
    comprehensiveSummary,
    expirationDate,
  } = data;

  // Validate data
  const validMaterialBreakdown = Array.isArray(materialBreakdown) ? materialBreakdown : [];

  const workDescriptionElements = parseHtmlContent(workDescription || '');
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  /* -------------------------------------------------------------------
     Derived flags – preview uses `showEquipmentSection`, etc., while the
     original PDF logic used `include*` flags.  This ensures we honour the
     exact toggle state coming from the dialog.
  ------------------------------------------------------------------- */
  const showMaterialsSection = settings.showMaterialsSection ?? true;
  const showQuotesSection = settings.showQuotesSection ?? true;
  const showLaborSection = settings.showLaborSection ?? true;
  const showEquipmentSection =
    settings.showEquipmentSection ?? settings.includeEquipmentCosts ?? false;
  const showSubcontractorSection =
    settings.showSubcontractorSection ?? settings.includeSubcontractorCosts ?? false;
  const showGeneralExpensesSection =
    settings.showGeneralExpensesSection ?? settings.includeGeneralExpenses ?? false;

  const showGlobalSupplyChainClause = settings.showGlobalSupplyChainClause ?? true;
  const showTariffsAndDutiesClause = settings.showTariffsAndDutiesClause ?? true;
  const showStandardContractTerms = settings.showStandardContractTerms ?? true;

  const showBaseCostColumn = settings.showBaseCostColumn ?? true;
  const showTaxDetails = settings.showTaxDetails ?? true;
  const showOverheadMarkup = settings.showOverheadMarkup ?? true;
  const showSubtotalInCostSummary = settings.showSubtotalInCostSummary ?? true;
  const showProjectTotalsBreakdown = settings.showProjectTotalsBreakdown ?? true;

  // Cost Summary Category Controls
  const showExtensionMaterialsInCostSummary = settings.showExtensionMaterialsInCostSummary ?? true;
  const showQuotedMaterialsInCostSummary = settings.showQuotedMaterialsInCostSummary ?? true;
  const showLaborInCostSummary = settings.showLaborInCostSummary ?? true;
  const showEquipmentInCostSummary = settings.showEquipmentInCostSummary ?? true;
  const showSubcontractorsInCostSummary = settings.showSubcontractorsInCostSummary ?? true;
  const showGeneralExpensesInCostSummary = settings.showGeneralExpensesInCostSummary ?? true;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <SmartSection sectionType="header">
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Text style={styles.companyName}>
                {accountData?.account?.accountname || companyName}
              </Text>
              {userData?.user && (
                <>
                  <Text style={styles.smallText}>Prepared by: {userData.user.name}</Text>
                  <Text style={styles.smallText}>{userData.user.email}</Text>
                </>
              )}
            </View>
            <View style={styles.headerRight}>
              {projectData?.address && (
                <>
                  <Text style={styles.boldText}>Project Address:</Text>
                  <Text style={styles.text}>{projectData.address}</Text>
                </>
              )}
              {!isEstimateContext && contractData && (
                <>
                  <Text style={styles.boldText}>Contract:</Text>
                  <Text style={styles.text}>{contractData.contractname || 'N/A'}</Text>
                  <Text style={styles.boldText}>Contract #:</Text>
                  <Text style={styles.text}>{contractData.customid || 'N/A'}</Text>
                </>
              )}
              <Text style={styles.boldText}>{isEstimateContext ? 'Estimate #' : 'PCO #'}:</Text>
              <Text style={styles.text}>{pcoNumber}</Text>
              <Text style={styles.boldText}>
                {isEstimateContext ? 'Estimate Name' : 'Change Order Name'}:
              </Text>
              <Text style={styles.text}>
                {(isEstimateContext ? estimateData?.name : changeOrderData?.name) || 'N/A'}
              </Text>
              {settings.showExpirationDate && expirationDate && (
                <>
                  <Text style={styles.boldText}>Expires:</Text>
                  <Text style={styles.text}>{expirationDate.toLocaleDateString('en-US')}</Text>
                </>
              )}
            </View>
          </View>
        </SmartSection>

        {/* Title */}
        <SmartSection sectionType="header">
          <Text style={styles.title}>
            {isEstimateContext ? 'Proposed Estimate' : 'Proposed Change Order'}
          </Text>
        </SmartSection>

        {/* Work Description */}
        <SmartSection sectionType="content">
          <Text style={styles.sectionHeader}>Work Description</Text>
          <View style={styles.sectionContent}>
            {workDescriptionElements.length > 0 ? (
              <RenderInstructionsToPdf
                instructions={workDescriptionElements}
                baseStyles={styles.workDescriptionParagraph}
              />
            ) : (
              <Text style={styles.text}>No work description provided.</Text>
            )}
          </View>
        </SmartSection>

        {/* Extension Materials Section */}
        {showMaterialsSection && validMaterialBreakdown.length > 0 && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>Extension Materials</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]} wrap={false}>
                <SmartTableCell width={settings.showExtensionBreakouts ? '25%' : '30%'} isHeader>
                  Description
                </SmartTableCell>
                {settings.showExtensionBreakouts && (
                  <SmartTableCell width="15%" isHeader>
                    Breakout
                  </SmartTableCell>
                )}
                <SmartTableCell
                  width={settings.showExtensionBreakouts ? '8%' : '10%'}
                  isHeader
                  align="right"
                >
                  Qty
                </SmartTableCell>
                <SmartTableCell width={settings.showExtensionBreakouts ? '8%' : '10%'} isHeader>
                  Unit
                </SmartTableCell>
                <SmartTableCell
                  width={settings.showExtensionBreakouts ? '12%' : '15%'}
                  isHeader
                  align="right"
                >
                  Unit Cost
                </SmartTableCell>
                <SmartTableCell
                  width={settings.showExtensionBreakouts ? '12%' : '15%'}
                  isHeader
                  align="right"
                >
                  Total Mat. $
                </SmartTableCell>
                <SmartTableCell width="10%" isHeader align="right">
                  Labor Hrs
                </SmartTableCell>
                <SmartTableCell width="5%" isHeader isLast align="center">
                  Status
                </SmartTableCell>
              </View>
              {validMaterialBreakdown.map((item: any, index: number) => (
                <View key={index} style={styles.tableRow} wrap={false}>
                  <SmartTableCell width={settings.showExtensionBreakouts ? '25%' : '30%'}>
                    {item.description || ''}
                  </SmartTableCell>
                  {settings.showExtensionBreakouts && (
                    <SmartTableCell width="15%">{item.breakout || ''}</SmartTableCell>
                  )}
                  <SmartTableCell
                    width={settings.showExtensionBreakouts ? '8%' : '10%'}
                    align="right"
                  >
                    {formatNumber(item.quantity || 0)}
                  </SmartTableCell>
                  <SmartTableCell width={settings.showExtensionBreakouts ? '8%' : '10%'}>
                    {item.unit || ''}
                  </SmartTableCell>
                  <SmartTableCell
                    width={settings.showExtensionBreakouts ? '12%' : '15%'}
                    align="right"
                  >
                    {formatCurrency(item.unitCost || 0)}
                  </SmartTableCell>
                  <SmartTableCell
                    width={settings.showExtensionBreakouts ? '12%' : '15%'}
                    align="right"
                  >
                    {formatCurrency(item.totalMaterialCost || 0)}
                  </SmartTableCell>
                  <SmartTableCell width="10%" align="right">
                    {formatNumber(item.totalLaborHours || 0)}
                  </SmartTableCell>
                  <SmartTableCell width="5%" isLast align="center">
                    {item.isQuoted ? 'Q' : 'E'}
                  </SmartTableCell>
                </View>
              ))}
              <View style={[styles.tableRow, styles.totalRow]} wrap={false}>
                <SmartTableCell
                  width={settings.showExtensionBreakouts ? '70%' : '65%'}
                  isHeader
                  isBold
                >
                  Totals
                </SmartTableCell>
                <SmartTableCell
                  width={settings.showExtensionBreakouts ? '12%' : '15%'}
                  isHeader
                  align="right"
                  isBold
                >
                  {formatCurrency(
                    validMaterialBreakdown.reduce(
                      (sum, item) => sum + (item.totalMaterialCost || 0),
                      0
                    )
                  )}
                </SmartTableCell>
                <SmartTableCell width="10%" isHeader align="right" isBold>
                  {formatNumber(
                    validMaterialBreakdown.reduce(
                      (sum, item) => sum + (item.totalLaborHours || 0),
                      0
                    )
                  )}
                </SmartTableCell>
                <SmartTableCell width="5%" isHeader isLast>
                  &nbsp;
                </SmartTableCell>
              </View>
            </View>
          </SmartSection>
        )}

        {/* Quoted Materials Section */}
        {showQuotesSection && quoteBreakdown && quoteBreakdown.length > 0 && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>Quoted Materials</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]} wrap={false}>
                <SmartTableCell width={settings.showNotesInTables ? '35%' : '40%'} isHeader>
                  Description
                </SmartTableCell>
                <SmartTableCell width="25%" isHeader align="right">
                  Quoted Cost
                </SmartTableCell>
                <SmartTableCell width="25%" isHeader align="right">
                  Total Cost
                </SmartTableCell>
                {settings.showNotesInTables ? (
                  <SmartTableCell width="15%" isHeader isLast>
                    Notes
                  </SmartTableCell>
                ) : (
                  <></>
                )}
              </View>
              {quoteBreakdown.map((item: any, index: number) => (
                <View key={index} style={styles.tableRow} wrap={false}>
                  <SmartTableCell width={settings.showNotesInTables ? '35%' : '40%'}>
                    {item.name}
                  </SmartTableCell>
                  <SmartTableCell width="25%" align="right">
                    {formatCurrency(item.quotedCost)}
                  </SmartTableCell>
                  <SmartTableCell width="25%" align="right">
                    {formatCurrency(item.totalCost)}
                  </SmartTableCell>
                  {settings.showNotesInTables && (
                    <SmartTableCell width="15%" isLast>
                      {item.notes || ''}
                    </SmartTableCell>
                  )}
                </View>
              ))}
              <View style={[styles.tableRow, styles.totalRow]} wrap={false}>
                <SmartTableCell width={settings.showNotesInTables ? '60%' : '70%'} isHeader isBold>
                  Total Quoted Materials
                </SmartTableCell>
                <SmartTableCell
                  width="25%"
                  isHeader
                  align="right"
                  isBold
                  isLast={!settings.showNotesInTables}
                >
                  {formatCurrency(
                    quoteBreakdown.reduce((sum: number, item: any) => sum + item.totalCost, 0)
                  )}
                </SmartTableCell>
                {settings.showNotesInTables && (
                  <SmartTableCell width="15%" isHeader isLast>
                    &nbsp;
                  </SmartTableCell>
                )}
              </View>
            </View>
          </SmartSection>
        )}

        {/* Labor Section */}
        {showLaborSection && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>Labor Details</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <SmartTableCell width="15%" isHeader>
                  Labor Type
                </SmartTableCell>
                <SmartTableCell width="25%" isHeader>
                  Name
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader align="right">
                  Hours
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader align="right">
                  Total Rate
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader align="right">
                  Fringe
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader isLast align="right">
                  Total Cost
                </SmartTableCell>
              </View>

              {/* Direct Labor */}
              {data?.closeoutData?.closeouts?.directlaborcloseout?.directlaborcloseoutList?.map(
                (labor: any, index: number) => (
                  <View key={`direct-${index}`} style={styles.tableRow}>
                    <SmartTableCell width="15%">Direct Labor</SmartTableCell>
                    <SmartTableCell width="25%">{labor.name}</SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {labor.allocatedhours?.toFixed(2) || '0.00'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {settings.includeLaborRates
                        ? formatCurrency(labor.laborrate * (1 + (labor.burdenpercent || 0) / 100))
                        : '-'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {formatCurrency(labor.fringe || 0)}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right" isLast>
                      {formatCurrency(labor.totalcost || 0)}
                    </SmartTableCell>
                  </View>
                )
              )}

              {/* Labor Factoring */}
              {data?.closeoutData?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList?.map(
                (labor: any, index: number) => (
                  <View key={`factoring-${index}`} style={styles.tableRow}>
                    <SmartTableCell width="15%">Labor Factoring</SmartTableCell>
                    <SmartTableCell width="25%">{labor.name}</SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {labor.allocatedhours?.toFixed(2) || '0.00'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {settings.includeLaborRates
                        ? formatCurrency(labor.laborrate * (1 + (labor.burdenpercent || 0) / 100))
                        : '-'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {formatCurrency(labor.fringe || 0)}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right" isLast>
                      {formatCurrency(labor.totalcost || 0)}
                    </SmartTableCell>
                  </View>
                )
              )}

              {/* Incidental Labor */}
              {data?.closeoutData?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList?.map(
                (labor: any, index: number) => (
                  <View key={`incidental-${index}`} style={styles.tableRow}>
                    <SmartTableCell width="15%">Incidental Labor</SmartTableCell>
                    <SmartTableCell width="25%">{labor.name}</SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {labor.allocatedhours?.toFixed(2) || '0.00'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {settings.includeLaborRates
                        ? formatCurrency(labor.laborrate * (1 + (labor.burdenpercent || 0) / 100))
                        : '-'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {formatCurrency(labor.fringe || 0)}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right" isLast>
                      {formatCurrency(labor.totalcost || 0)}
                    </SmartTableCell>
                  </View>
                )
              )}

              {/* Indirect Labor */}
              {data?.closeoutData?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList?.map(
                (labor: any, index: number) => (
                  <View key={`indirect-${index}`} style={styles.tableRow}>
                    <SmartTableCell width="15%">Indirect Labor</SmartTableCell>
                    <SmartTableCell width="25%">{labor.name}</SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {labor.allocatedhours?.toFixed(2) || '0.00'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {settings.includeLaborRates
                        ? formatCurrency(labor.laborrate * (1 + (labor.burdenpercent || 0) / 100))
                        : '-'}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {formatCurrency(labor.fringe || 0)}
                    </SmartTableCell>
                    <SmartTableCell width="15%" align="right" isLast>
                      {formatCurrency(labor.totalcost || 0)}
                    </SmartTableCell>
                  </View>
                )
              )}

              {/* Total Labor Row */}
              <View style={[styles.tableRow, styles.totalRow]}>
                <SmartTableCell width="85%" isHeader isBold>
                  Total Labor
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader align="right" isLast isBold>
                  {formatCurrency(comprehensiveSummary?.adjustedLaborTotal || 0)}
                </SmartTableCell>
              </View>
            </View>
          </SmartSection>
        )}

        {/* Equipment Section */}
        {showEquipmentSection && equipmentBreakdown && equipmentBreakdown.length > 0 && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>Equipment</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <SmartTableCell width="30%" isHeader>
                  Equipment Name
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader align="right">
                  Quantity
                </SmartTableCell>
                <SmartTableCell width="15%" isHeader align="right">
                  Duration
                </SmartTableCell>
                <SmartTableCell width="20%" isHeader align="right">
                  Unit Cost
                </SmartTableCell>
                <SmartTableCell width="20%" isHeader isLast align="right">
                  Total Cost
                </SmartTableCell>
              </View>
              {equipmentBreakdown.map((item: any, index: number) => (
                <View key={index} style={styles.tableRow}>
                  <SmartTableCell width="30%">{item.name}</SmartTableCell>
                  <SmartTableCell width="15%" align="right">
                    {item.quantity?.toFixed(1) || '0.0'}
                  </SmartTableCell>
                  <SmartTableCell width="15%" align="right">
                    {item.duration?.toFixed(1) || '0.0'}
                  </SmartTableCell>
                  <SmartTableCell width="20%" align="right">
                    {formatCurrency(item.unitCost)}
                  </SmartTableCell>
                  <SmartTableCell width="20%" align="right" isLast>
                    {formatCurrency(item.totalCost)}
                  </SmartTableCell>
                </View>
              ))}
              <View style={[styles.tableRow, styles.totalRow]}>
                <SmartTableCell width="80%" isHeader isBold>
                  Total Equipment
                </SmartTableCell>
                <SmartTableCell width="20%" isHeader isLast align="right" isBold>
                  {formatCurrency(
                    equipmentBreakdown.reduce((sum: number, item: any) => sum + item.totalCost, 0)
                  )}
                </SmartTableCell>
              </View>
            </View>
          </SmartSection>
        )}

        {/* Subcontractor Section */}
        {showSubcontractorSection &&
          subcontractorBreakdown &&
          subcontractorBreakdown.length > 0 && (
            <SmartSection sectionType="content">
              <Text style={styles.sectionHeader}>Subcontractors</Text>
              <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <SmartTableCell width="40%" isHeader>
                    Subcontractor
                  </SmartTableCell>
                  <SmartTableCell width="30%" isHeader align="right">
                    Quoted Amount
                  </SmartTableCell>
                  <SmartTableCell width="30%" isHeader isLast align="right">
                    Total Cost
                  </SmartTableCell>
                </View>
                {subcontractorBreakdown.map((item: any, index: number) => (
                  <View key={index} style={styles.tableRow}>
                    <SmartTableCell width="40%">{item.name}</SmartTableCell>
                    <SmartTableCell width="30%" align="right">
                      {formatCurrency(item.quotedAmount)}
                    </SmartTableCell>
                    <SmartTableCell width="30%" align="right" isLast>
                      {formatCurrency(item.totalCost)}
                    </SmartTableCell>
                  </View>
                ))}
                <View style={[styles.tableRow, styles.totalRow]}>
                  <SmartTableCell width="70%" isHeader isBold>
                    Total Subcontractors
                  </SmartTableCell>
                  <SmartTableCell width="30%" isHeader isLast align="right" isBold>
                    {formatCurrency(
                      subcontractorBreakdown.reduce(
                        (sum: number, item: any) => sum + item.totalCost,
                        0
                      )
                    )}
                  </SmartTableCell>
                </View>
              </View>
            </SmartSection>
          )}

        {/* General Expenses Section */}
        {showGeneralExpensesSection &&
          generalExpenseBreakdown &&
          generalExpenseBreakdown.length > 0 && (
            <SmartSection sectionType="content">
              <Text style={styles.sectionHeader}>General Expenses</Text>
              <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <SmartTableCell width="40%" isHeader>
                    Description
                  </SmartTableCell>
                  <SmartTableCell width="15%" isHeader align="right">
                    Quantity
                  </SmartTableCell>
                  <SmartTableCell width="20%" isHeader align="right">
                    Unit Cost
                  </SmartTableCell>
                  <SmartTableCell width="25%" isHeader isLast align="right">
                    Total Cost
                  </SmartTableCell>
                </View>
                {generalExpenseBreakdown.map((item: any, index: number) => (
                  <View key={index} style={styles.tableRow}>
                    <SmartTableCell width="40%">{item.name}</SmartTableCell>
                    <SmartTableCell width="15%" align="right">
                      {item.quantity?.toFixed(1) || '0.0'}
                    </SmartTableCell>
                    <SmartTableCell width="20%" align="right">
                      {formatCurrency(item.unitCost)}
                    </SmartTableCell>
                    <SmartTableCell width="25%" align="right" isLast>
                      {formatCurrency(item.totalCost)}
                    </SmartTableCell>
                  </View>
                ))}
                <View style={[styles.tableRow, styles.totalRow]}>
                  <SmartTableCell width="75%" isHeader isBold>
                    Total General Expenses
                  </SmartTableCell>
                  <SmartTableCell width="25%" isHeader isLast align="right" isBold>
                    {formatCurrency(
                      generalExpenseBreakdown.reduce(
                        (sum: number, item: any) => sum + item.totalCost,
                        0
                      )
                    )}
                  </SmartTableCell>
                </View>
              </View>
            </SmartSection>
          )}

        {/* COST SUMMARY – now placed before any contractual clauses */}
        {comprehensiveSummary && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>Cost Summary</Text>
            <View style={styles.table}>
              {/* Show detailed header only if any category toggles are enabled */}
              {(showExtensionMaterialsInCostSummary ||
                showQuotedMaterialsInCostSummary ||
                showLaborInCostSummary ||
                showEquipmentInCostSummary ||
                showSubcontractorsInCostSummary ||
                showGeneralExpensesInCostSummary) && (
                <View style={[styles.tableRow, styles.tableHeader]} wrap={false}>
                  <SmartTableCell width="35%" isHeader>
                    Cost Category
                  </SmartTableCell>
                  {showBaseCostColumn && (
                    <SmartTableCell width="20%" isHeader align="right">
                      Base Cost
                    </SmartTableCell>
                  )}
                  {(showTaxDetails || showOverheadMarkup) && (
                    <SmartTableCell width="25%" isHeader align="right">
                      {showTaxDetails && showOverheadMarkup
                        ? 'Tax & Markup'
                        : showTaxDetails
                          ? 'Tax'
                          : 'Markup'}
                    </SmartTableCell>
                  )}
                  <SmartTableCell width="20%" isHeader isLast align="right">
                    Total Cost
                  </SmartTableCell>
                </View>
              )}

              {/* Cost Category Rows - Conditionally rendered */}
              {(
                [
                  {
                    label: 'Extension Materials',
                    base: comprehensiveSummary.materialTotal,
                    tax: comprehensiveSummary.materialTax,
                    overhead: comprehensiveSummary.materialOverhead,
                    markup: comprehensiveSummary.materialMarkup,
                    total: comprehensiveSummary.adjustedMaterialTotal,
                    taxable: true,
                    show: showExtensionMaterialsInCostSummary,
                  },
                  {
                    label: 'Quoted Materials',
                    base: comprehensiveSummary.quoteTotal,
                    tax: comprehensiveSummary.quoteTax,
                    overhead: comprehensiveSummary.quoteOverhead,
                    markup: comprehensiveSummary.quoteMarkup,
                    total: comprehensiveSummary.adjustedQuoteTotal,
                    taxable: true,
                    show: showQuotedMaterialsInCostSummary,
                  },
                  {
                    label: 'Labor',
                    base: comprehensiveSummary.totalBaseLaborCost,
                    tax: comprehensiveSummary.laborTax,
                    overhead: comprehensiveSummary.laborOverhead,
                    markup: comprehensiveSummary.laborMarkup,
                    total: comprehensiveSummary.adjustedLaborTotal,
                    taxable: false,
                    show: showLaborInCostSummary,
                  },
                  {
                    label: 'Equipment',
                    base: comprehensiveSummary.equipmentTotal,
                    tax: comprehensiveSummary.equipmentTax,
                    overhead: comprehensiveSummary.equipmentOverhead,
                    markup: comprehensiveSummary.equipmentMarkup,
                    total: comprehensiveSummary.adjustedEquipmentTotal,
                    taxable: true,
                    show: showEquipmentInCostSummary,
                  },
                  {
                    label: 'Subcontractors',
                    base: comprehensiveSummary.subcontractorTotal,
                    tax: comprehensiveSummary.subcontractorTax,
                    overhead: comprehensiveSummary.subcontractorOverhead,
                    markup: comprehensiveSummary.subcontractorMarkup,
                    total: comprehensiveSummary.adjustedSubcontractorTotal,
                    taxable: false,
                    show: showSubcontractorsInCostSummary,
                  },
                  {
                    label: 'General Expenses',
                    base: comprehensiveSummary.generalExpenseTotal,
                    tax: comprehensiveSummary.generalExpenseTax,
                    overhead: comprehensiveSummary.generalExpenseOverhead,
                    markup: comprehensiveSummary.generalExpenseMarkup,
                    total: comprehensiveSummary.adjustedGeneralExpenseTotal,
                    taxable: false,
                    show: showGeneralExpensesInCostSummary,
                  },
                ] as const
              ).map((row, idx) =>
                row.show && row.total > 0 ? (
                  <View key={idx} style={styles.tableRow} wrap={false}>
                    <SmartTableCell width="35%">{row.label}</SmartTableCell>
                    {showBaseCostColumn && (
                      <SmartTableCell width="20%" align="right">
                        {formatCurrency(row.base + (showTaxDetails ? 0 : row.tax || 0))}
                      </SmartTableCell>
                    )}
                    {(showTaxDetails || showOverheadMarkup) && (
                      <SmartTableCell width="25%" align="right">
                        <View>
                          {showTaxDetails && row.taxable && (
                            <Text style={styles.tableCellText}>
                              Tax: {formatCurrency(Number(row.tax) || 0)}
                            </Text>
                          )}
                          {showOverheadMarkup && (
                            <>
                              <Text style={styles.tableCellText}>
                                Overhead: {formatCurrency(Number(row.overhead) || 0)}
                              </Text>
                              <Text style={styles.tableCellText}>
                                Markup: {formatCurrency(Number(row.markup) || 0)}
                              </Text>
                            </>
                          )}
                        </View>
                      </SmartTableCell>
                    )}
                    <SmartTableCell width="20%" align="right" isLast>
                      {formatCurrency(row.total)}
                    </SmartTableCell>
                  </View>
                ) : null
              )}

              {/* Optional Subtotal */}
              {showSubtotalInCostSummary && (
                <View style={[styles.tableRow, styles.totalRow]} wrap={false}>
                  <SmartTableCell width="80%" isBold>
                    Subtotal
                  </SmartTableCell>
                  <SmartTableCell width="20%" align="right" isLast isBold>
                    {formatCurrency(comprehensiveSummary.subtotal)}
                  </SmartTableCell>
                </View>
              )}

              {/* Project OH & Profit */}
              {showProjectTotalsBreakdown && (
                <>
                  {comprehensiveSummary.projectOverhead > 0 && (
                    <View style={styles.tableRow} wrap={false}>
                      <SmartTableCell width="80%">Project Overhead</SmartTableCell>
                      <SmartTableCell width="20%" align="right" isLast>
                        {formatCurrency(comprehensiveSummary.projectOverhead)}
                      </SmartTableCell>
                    </View>
                  )}
                  {comprehensiveSummary.profit > 0 && (
                    <View style={styles.tableRow} wrap={false}>
                      <SmartTableCell width="80%">Project Profit</SmartTableCell>
                      <SmartTableCell width="20%" align="right" isLast>
                        {formatCurrency(comprehensiveSummary.profit)}
                      </SmartTableCell>
                    </View>
                  )}
                </>
              )}

              {/* Grand Total - Always shown */}
              <View style={[styles.tableRow, styles.totalRow]} wrap={false}>
                <SmartTableCell width="80%" isBold>
                  {data?.closeoutData?.closeouts?.sellingprice ? 'SELLING PRICE' : 'TOTAL AMOUNT'}
                </SmartTableCell>
                <SmartTableCell width="20%" align="right" isLast isBold>
                  {formatCurrency(
                    data?.closeoutData?.closeouts?.sellingprice || comprehensiveSummary.grandTotal
                  )}
                </SmartTableCell>
              </View>
            </View>
          </SmartSection>
        )}

        {/* CONTRACTUAL CLAUSES – separate headings to match preview */}
        {showGlobalSupplyChainClause && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>{globalSupplyChainClause.heading}</Text>
            <View style={styles.sectionContent}>
              {globalSupplyChainClause.bullets?.map((bullet, index) => (
                <Text key={index} style={styles.text}>
                  • {bullet}
                </Text>
              ))}
            </View>
          </SmartSection>
        )}

        {showTariffsAndDutiesClause && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>{tariffsAndDutiesClause.heading}</Text>
            <View style={styles.sectionContent}>
              {tariffsAndDutiesClause.bullets?.map((bullet, index) => (
                <Text key={index} style={styles.text}>
                  • {bullet}
                </Text>
              ))}
            </View>
          </SmartSection>
        )}

        {showStandardContractTerms && (
          <SmartSection sectionType="content">
            <Text style={styles.sectionHeader}>{standardTermsClause.heading}</Text>
            <View style={styles.sectionContent}>
              {standardTermsClause.paragraphs?.map((paragraph, index) => (
                <Text key={index} style={styles.text}>
                  {paragraph}
                </Text>
              ))}
            </View>
          </SmartSection>
        )}

        {/* Signatures */}
        <SmartSection sectionType="signature">
          {/* Contractor Certification */}
          <View style={styles.signatureSection}>
            <Text style={styles.boldText}>Contractor Certification</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.smallText}>
              I hereby certify that this quotation is complete and accurate based on the information
              provided
            </Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
              <Text style={styles.text}>Name: _______________________</Text>
              <Text style={styles.text}>Date: _______________________</Text>
            </View>
          </View>

          {/* Client Acceptance */}
          <View style={styles.signatureSection}>
            <Text style={styles.boldText}>Client Acceptance</Text>
            <Text style={styles.text}>
              {isEstimateContext ? 'Estimate' : 'PCO'} #{pcoNumber} • Final Amount:{' '}
              {formatCurrency(comprehensiveSummary?.grandTotal || 0)}
            </Text>
            <View style={styles.signatureLine} />
            <Text style={styles.smallText}>
              I hereby accept this quotation and authorize the contractor to complete the above
              described work
            </Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
              <Text style={styles.text}>Name: _______________________</Text>
              <Text style={styles.text}>Date: _______________________</Text>
            </View>
          </View>
        </SmartSection>

        {/* Footer */}
        <SmartSection sectionType="content">
          <View style={styles.footer}>
            <Text style={styles.smallText}>Report Generated: {currentDate}</Text>
          </View>
        </SmartSection>
      </Page>
    </Document>
  );
};

// Export function to generate PDF blob
export const generatePDFBlob = async (data: any, settings: any): Promise<Blob> => {
  const doc = <ChangeOrderReportPDF data={data} settings={settings} />;
  const blob = await pdf(doc).toBlob();
  return blob;
};

// Export function to generate PDF download
export const downloadPDF = async (data: any, settings: any, filename: string) => {
  const blob = await generatePDFBlob(data, settings);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
